<template>
  <div class="fm-select" :class="{ 'fm-select--open': open, 'fm-select--disabled': disabled }" ref="root">

    <!--
      Hidden native <select> — never visible but lets FormGroup's querySelectorAll find
      it so fm-has-value detection and the floating-label animation both work correctly.
    -->
    <select ref="native" class="fm-select__native" :disabled="disabled" tabindex="-1" aria-hidden="true">
      <option value=""></option>
      <option v-for="opt in normalizedOptions" :key="opt.value" :value="opt.value" v-text="opt.label"/>
    </select>

    <!-- Visible trigger — shares .form-control so it inherits FloatingLabel padding -->
    <div
      class="fm-select__trigger form-control"
      :class="{ 'fm-select__trigger--empty': !hasValue }"
      :tabindex="disabled ? -1 : 0"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="open"
      :aria-controls="listboxId"
      :aria-activedescendant="open && highlightedIndex >= 0 ? optionId(highlightedIndex) : null"
      @click="!disabled && toggle()"
      @keydown.enter.prevent="!disabled && onEnter()"
      @keydown.space.prevent="!disabled && onEnter()"
      @keydown.down.prevent="!disabled && onArrow(1)"
      @keydown.up.prevent="!disabled && onArrow(-1)"
      @keydown.escape="close"
      @keydown.tab="close"
    >
      <span v-if="currentIcon" class="fm-select__sel-icon" v-html="currentIcon"/>
      <span class="fm-select__sel-text">{{ currentLabel }}</span>
      <svg class="fm-select__chevron" :class="{ 'fm-select__chevron--up': open }"
           width="11" height="7" viewBox="0 0 11 7" fill="none" aria-hidden="true">
        <path d="M1 1l4.5 5 4.5-5" stroke="currentColor" stroke-width="1.6"
              stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>

    <!-- Dropdown panel — position:fixed via :style so it escapes ancestor overflow:hidden -->
    <transition name="fm-drop">
      <div v-if="open" :id="listboxId" class="fm-select__menu" :style="menuStyle" role="listbox">
        <div
          v-for="(opt, idx) in normalizedOptions"
          :id="optionId(idx)"
          :key="opt.value"
          class="fm-select__item"
          :class="{
            'fm-select__item--active': isSelected(opt.value),
            'fm-select__item--highlighted': idx === highlightedIndex,
          }"
          role="option"
          :aria-selected="isSelected(opt.value)"
          @mouseenter="highlightedIndex = idx"
          @click="pick(opt.value)"
        >
          <span class="fm-select__item-icon" v-html="iconFor(opt.value)"/>
          <span class="fm-select__item-label">{{ opt.label }}</span>
          <svg v-if="isSelected(opt.value)" class="fm-select__item-check"
               width="13" height="10" viewBox="0 0 13 10" fill="none" aria-hidden="true">
            <path d="M1.5 5L5 8.5L11.5 1.5" stroke="#FFB000" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
// Fallback icon shown when no specific icon is provided for an option value
const ICON_DEFAULT = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"/><path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 14.5a6.5 6.5 0 1 1 0-13 6.5 6.5 0 0 1 0 13z"/></svg>`;

let fmSelectInstanceCounter = 0;

export default {
  name: 'FmSelect',
  model: { prop: 'value', event: 'input' },
  props: {
    /** Current selected value — bound via v-model */
    value: { default: null },
    /**
     * Options as:
     *   • Object  { key: 'Label', ... }
     *   • Array   [val1, val2, ...]  (value === label === element)
     */
    options: { required: true },
    /**
     * Per-option SVG icon strings keyed by option value.
     * When omitted or a key is missing, ICON_DEFAULT is used.
     */
    icons: { type: Object, default: null },
    placeholder: { type: String, default: 'Select' },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      open: false,
      menuStyle: {},
      highlightedIndex: -1,
      // Unique per-instance id so aria-activedescendant/aria-controls can
      // reference this instance's option rows/listbox without colliding
      // with any other FmSelect on the same page.
      instanceId: `fm-select-${++fmSelectInstanceCounter}`,
    };
  },
  computed: {
    proxyValue: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },
    normalizedOptions() {
      if (Array.isArray(this.options)) {
        return this.options.map(v => ({ value: v, label: String(v) }));
      }
      return Object.entries(this.options).map(([value, label]) => ({ value, label }));
    },
    hasValue() {
      return this.proxyValue !== null && this.proxyValue !== undefined && this.proxyValue !== '';
    },
    currentLabel() {
      if (!this.hasValue) return '';
      const opt = this.normalizedOptions.find(o => String(o.value) === String(this.proxyValue));
      return opt ? opt.label : String(this.proxyValue);
    },
    currentIcon() {
      if (!this.hasValue) return null;
      return this.iconFor(this.proxyValue);
    },
    listboxId() {
      return `${this.instanceId}-listbox`;
    },
  },
  methods: {
    isSelected(val) {
      return String(val) === String(this.proxyValue);
    },
    iconFor(val) {
      if (this.icons && this.icons[val] !== undefined) return this.icons[val];
      return ICON_DEFAULT;
    },
    optionId(idx) {
      return `${this.instanceId}-opt-${idx}`;
    },
    selectedIndex() {
      const idx = this.normalizedOptions.findIndex(o => this.isSelected(o.value));
      return idx >= 0 ? idx : 0;
    },
    _calcMenuStyle() {
      const trigger = this.$el.querySelector('.fm-select__trigger');
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();
      this.menuStyle = {
        position: 'fixed',
        top:   `${r.bottom + 6}px`,
        left:  `${r.left}px`,
        width: `${r.width}px`,
      };
    },
    toggle() {
      if (this.open) { this.open = false; return; }
      this._calcMenuStyle();
      this.highlightedIndex = this.selectedIndex();
      this.open = true;
    },
    close() { this.open = false; },
    // Enter/Space: open the menu if closed, otherwise commit the
    // currently-highlighted option — mirrors native <select>/combobox behaviour.
    onEnter() {
      if (this.open) {
        this.pickHighlighted();
      } else {
        this.toggle();
      }
    },
    // Arrow keys: open (and highlight the current selection) if closed,
    // otherwise move the highlight up/down within the option list.
    onArrow(delta) {
      if (!this.open) {
        this._calcMenuStyle();
        this.highlightedIndex = this.selectedIndex();
        this.open = true;
        return;
      }
      const max = this.normalizedOptions.length - 1;
      if (max < 0) return;
      const next = this.highlightedIndex + delta;
      this.highlightedIndex = Math.min(max, Math.max(0, next));
    },
    pickHighlighted() {
      const opt = this.normalizedOptions[this.highlightedIndex];
      if (opt) this.pick(opt.value);
    },
    pick(val) {
      this.proxyValue = val;
      this.open = false;
      // Sync hidden native select + fire change event so FormGroup updates fm-has-value
      this.$nextTick(() => {
        if (this.$refs.native) {
          this.$refs.native.value = String(val);
          this.$refs.native.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    },
    syncNative(val) {
      if (this.$refs.native) {
        this.$refs.native.value = (val !== null && val !== undefined) ? String(val) : '';
      }
    },
  },
  watch: {
    // Keep hidden select in sync when value is set externally (e.g. draft restore)
    value(val) { this.syncNative(val); },
  },
  mounted() {
    // Initialise hidden select — FormGroup reads it after its own $nextTick
    this.syncNative(this.proxyValue);
    // Close on outside click
    this._onOutside = (e) => { if (!this.$el.contains(e.target)) this.close(); };
    document.addEventListener('click', this._onOutside, true);
    // Recompute fixed position while open (scroll / resize)
    this._onReposition = () => { if (this.open) this._calcMenuStyle(); };
    window.addEventListener('scroll', this._onReposition, { passive: true, capture: true });
    window.addEventListener('resize', this._onReposition, { passive: true });
  },
  beforeDestroy() {
    document.removeEventListener('click', this._onOutside, true);
    window.removeEventListener('scroll', this._onReposition, true);
    window.removeEventListener('resize', this._onReposition);
  },
};
</script>

<style>
/* ── FmSelect ─────────────────────────────────────────────────────────────── */

.fm-select { position: relative; }

/* Hidden native select — zero-size but queryable by FormGroup */
.fm-select__native {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
  border: none;
}

/* ── Trigger ──────────────────────────────────────────────────────────────── */
.fm-select__trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  position: relative;
  padding-right: 36px !important;   /* room for the chevron */
}

.fm-select__trigger--empty .fm-select__sel-text {
  color: transparent; /* hide placeholder text — floating label fills the space */
}

/* When the dropdown is open, give the trigger an amber focus ring */
.fm-select--open .fm-select__trigger {
  border-color: #FFB000;
  box-shadow: 0 0 0 3px rgba(255, 176, 0, 0.12);
  outline: none;
}

.fm-select__sel-icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: #9197A1;
}
.fm-select__sel-icon svg { width: 15px; height: 15px; }

.fm-select__sel-text {
  flex: 1;
  font-size: 15px;
  color: #15171C;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

/* Animated chevron */
.fm-select__chevron {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9197A1;
  transition: transform 0.2s ease, color 0.2s ease;
  pointer-events: none;
}
.fm-select__chevron--up {
  transform: translateY(-50%) rotate(180deg);
  color: #FFB000;
}

/* ── Dropdown panel ───────────────────────────────────────────────────────── */
/* position/top/left/width set via inline :style (fixed coords from getBoundingClientRect)
   so the menu escapes any ancestor overflow:hidden (e.g. .fm-card). */
.fm-select__menu {
  background: #fff;
  border: 1.5px solid #E7E4DA;
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(21, 23, 28, 0.13), 0 2px 8px rgba(21, 23, 28, 0.06);
  z-index: 1060;
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;
}

.fm-select__menu::-webkit-scrollbar { width: 4px; }
.fm-select__menu::-webkit-scrollbar-track { background: transparent; }
.fm-select__menu::-webkit-scrollbar-thumb { background: #E0DDD5; border-radius: 4px; }

/* ── Option row ───────────────────────────────────────────────────────────── */
.fm-select__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: #15171C;
  transition: background 0.1s;
}
.fm-select__item + .fm-select__item {
  border-top: 1px solid #F0EEE7;
}
.fm-select__item:hover { background: #FBFAF6; }
.fm-select__item--active {
  background: rgba(255, 176, 0, 0.06);
  font-weight: 500;
}
.fm-select__item--active:hover { background: rgba(255, 176, 0, 0.10); }

/* Keyboard-highlighted row (arrow-key navigation) — distinct from hover/active
   so keyboard users can see which option Enter will commit. */
.fm-select__item--highlighted {
  background: rgba(255, 176, 0, 0.14);
  box-shadow: inset 0 0 0 1.5px rgba(255, 176, 0, 0.5);
}

/* Icon chip */
.fm-select__item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #F5F4EF;
  flex-shrink: 0;
  color: #5B606B;
}
.fm-select__item-icon svg { width: 14px; height: 14px; }
.fm-select__item--active .fm-select__item-icon {
  background: rgba(255, 176, 0, 0.14);
  color: #C8880A;
}

.fm-select__item-label { flex: 1; }
.fm-select__item-check { flex-shrink: 0; }

/* ── Open/close animation ─────────────────────────────────────────────────── */
.fm-drop-enter-active, .fm-drop-leave-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
}
.fm-drop-enter, .fm-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
  transform-origin: top center;
}

/* ── Floating label context adjustments ──────────────────────────────────── */
/* In a floating FormGroup the .form-control gets padding-top: 1.55rem.
   Flex align-items:center centres within the content area below that padding,
   which lines up naturally with the floated label above. */
.form-group--floating .fm-select__trigger {
  padding-bottom: 0.5rem;
}
</style>
